import React from "react";

import Spinner from "../Spinner/Spinner";

import "./Button.style.scss";

export type ButtonVariant = "contained" | "outlined" | "text";
export type ButtonSize = "xs" | "sm" | "md" | "lg";
export type ButtonRounded = "xs" | "sm" | "md" | "lg" | "full";

/** Named colours resolve to design tokens; anything else is passed straight
 *  through, so color="#efefef" works as well as color="primary". */
const NAMED_COLORS: Record<string, string> = {
  primary: "var(--jade)",
  success: "var(--good)",
  error: "var(--danger)",
  // Deliberately a dark neutral rather than the care triad's teal, which
  // carries a different meaning in this app.
  info: "var(--ink-2)",
  warning: "var(--warn)",
};

/**
 * A custom colour can be any lightness, so a filled button has to work out
 * whether its label should be dark or light. Tokens are left to CSS, which
 * cannot be measured here - they are all dark enough to carry white.
 */
const readableOn = (color: string): string | undefined => {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!hex) return undefined;

  let value = hex[1];
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }

  const channels = [0, 2, 4].map((i) => {
    const channel = parseInt(value.substr(i, 2), 16) / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4);
  });

  const luminance =
    0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];

  // 0.4 puts the switch near where white and black contrast equally.
  return luminance > 0.4 ? "var(--ink)" : "#fff";
};

interface Props {
  children?: React.ReactNode;
  className?: string;
  variant?: ButtonVariant;
  color?: "primary" | "success" | "error" | "info" | "warning" | string;
  size?: ButtonSize;
  disabled?: boolean;
  /** Shows a spinner after the label; the button stops responding while set. */
  loading?: boolean;
  iconStart?: React.ReactNode;
  iconEnd?: React.ReactNode;
  fullWidth?: boolean;
  /** Corner radius. "full" gives a pill, for chips and the like. */
  rounded?: ButtonRounded;
  type?: "button" | "submit" | "reset";
  title?: string;
  style?: React.CSSProperties;
  onClick?: (params: any) => any;
}

const Button: React.FC<Props> = ({
  children,
  className = "",
  variant = "contained",
  color = "primary",
  size = "md",
  disabled = false,
  loading = false,
  iconStart,
  iconEnd,
  fullWidth = false,
  rounded = "md",
  type = "button",
  title,
  style,
  onClick,
}) => {
  const resolvedColor = NAMED_COLORS[color] || color;
  const contrast =
    variant === "contained" ? readableOn(resolvedColor) : undefined;

  return (
    <button
      type={type}
      title={title}
      className={[
        "btn",
        `btn-${variant}`,
        `btn-${size}`,
        fullWidth ? "btn-full" : "",
        `btn-r-${rounded}`,
        loading ? "btn-loading" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        {
          "--btn-color": resolvedColor,
          ...(contrast ? { "--btn-on-color": contrast } : {}),
          ...style,
        } as React.CSSProperties
      }
      // A button mid-request is not clickable, but stays distinct from a
      // disabled one: it keeps its colour rather than greying out.
      disabled={disabled || loading}
      onClick={onClick}
    >
      {iconStart && <span className="btn-icon">{iconStart}</span>}
      {children}
      {iconEnd && <span className="btn-icon">{iconEnd}</span>}
      {loading && (
        <Spinner className="btn-spinner" size="small" color="currentColor" />
      )}
    </button>
  );
};

export default Button;
